/**
 * The cap and the standby list (1.8.3, D-1.8.3-6 — Elijah, 9 Oct 2026): "If you need 20, it goes out saying we need
 * 20, and only 20 can sign up. Everyone else: would you like to go on the standby list, in case another volunteer
 * can't make it?"
 *
 * One rule for the three places volunteers answer a request sent to many: an event's volunteers, a group outing and
 * Volunteer help (an activity's date). The count stops at the number needed; whoever answers after that is asked the
 * standby question on the same page; the office sees the standby list in the order people joined; when someone who
 * held a place drops out, "Offer to the next on standby" is one press by a person (1.7.4's waiting-list rule: an offer
 * waits in the Outbox, is audited, and nothing moves by itself).
 *
 * Pure rules and the words in both languages. Nothing here reads or writes a list.
 */

export type StandbyKind = 'event' | 'outing' | 'activity';
export type StandbyLang = 'en' | 'es';

/** What a volunteer's press on "I can help" becomes. `needed` null = no number was set: everyone who says yes holds a place. */
export function placeOrStandby(needed: number | null, holding: number): 'place' | 'full' {
  if (needed === null) return 'place';
  return holding < Math.max(0, Math.floor(needed)) ? 'place' : 'full';
}

/** How many places are still open (never below zero; null = no number was set). */
export const placesLeft = (needed: number | null, holding: number): number | null => (needed === null ? null : Math.max(0, Math.floor(needed) - holding));

/** The standby list in the order people joined (the earliest first; a tie keeps the given order). */
export function standbyOrder<T extends { joinedAt: string }>(rows: readonly T[]): T[] {
  return rows
    .map((r, i) => ({ r, i }))
    .sort((a, b) => (a.r.joinedAt < b.r.joinedAt ? -1 : a.r.joinedAt > b.r.joinedAt ? 1 : a.i - b.i))
    .map((x) => x.r);
}

/** Who "Offer to the next on standby" offers to: the earliest on the list not offered yet (null = nobody is waiting). */
export function nextOnStandby<T extends { joinedAt: string; offeredAt?: string | null }>(rows: readonly T[]): T | null {
  return standbyOrder(rows).find((r) => !r.offeredAt) ?? null;
}

const NOUN: Record<StandbyLang, Record<StandbyKind, string>> = {
  en: { event: 'event', outing: 'outing', activity: 'activity' },
  es: { event: 'este evento', outing: 'esta salida', activity: 'esta actividad' },
};

/** "We need 20 volunteers for the Fall Fest Celebration on Saturday, October 24." */
export function needLine(needed: number, name: string, dayWords: string, lang: StandbyLang): string {
  const n = Math.max(1, Math.floor(needed));
  if (lang === 'es') return `Necesitamos ${n} ${n === 1 ? 'voluntario' : 'voluntarios'} para ${name} el ${dayWords}.`;
  return `We need ${n} ${n === 1 ? 'volunteer' : 'volunteers'} for the ${name} on ${dayWords}.`;
}

/** The question a volunteer sees when the number is already reached, with its two buttons. */
export function standbyQuestion(kind: StandbyKind, lang: StandbyLang): { question: string; yes: string; no: string } {
  if (lang === 'es') return { question: `Ya tenemos suficientes voluntarios para ${NOUN.es[kind]}. ¿Le gustaría quedar en la lista de reserva, por si alguien no puede asistir?`, yes: 'Sí, agrégueme', no: 'No, gracias' };
  return { question: `We have enough volunteers for this ${NOUN.en[kind]}. Would you like to go on the standby list, in case someone can't make it?`, yes: 'Yes, add me', no: 'No, thank you' };
}

/** What the page says after the answer to the standby question. */
export function standbyAnswered(answer: 'yes' | 'no', lang: StandbyLang): string {
  if (lang === 'es') return answer === 'yes' ? 'Está en la lista de reserva. Si se abre un lugar, la oficina le escribirá.' : 'Gracias por responder. No está en la lista de reserva.';
  return answer === 'yes' ? "You're on the standby list. If a place opens, the office will write to you." : "Thank you for answering. You're not on the standby list.";
}

/** The office's button, and the line it leaves. */
export const OFFER_NEXT_WORDS = {
  en: { button: 'Offer to the next on standby', none: 'Nobody is on the standby list.', title: 'Standby list', order: 'In the order they joined' },
  es: { button: 'Ofrecer al siguiente en la lista de reserva', none: 'No hay nadie en la lista de reserva.', title: 'Lista de reserva', order: 'En el orden en que se anotaron' },
} as const;

/**
 * The offer to the next on standby, in words (E, 1.8.3): the paragraph the email carries, the line the volunteer's
 * page shows while a place is open for them, and what the page says when someone else confirmed first. The email
 * asks for a press on its button, never for a reply.
 */
export function standbyOffer(kind: StandbyKind, lang: StandbyLang): { email: string; page: string; taken: string } {
  if (lang === 'es')
    return {
      email: `Se abrió un lugar para ${NOUN.es[kind]} y usted sigue en la lista de reserva. Si todavía puede ayudar, pulse el botón de abajo para confirmar. El lugar es suyo cuando confirme.`,
      page: `Se abrió un lugar para ${NOUN.es[kind]}. ¿Todavía puede ayudar?`,
      taken: 'Gracias por responder. Ese lugar ya se ocupó; usted sigue en la lista de reserva.',
    };
  return {
    email: `A place has opened for this ${NOUN.en[kind]}, and you are next on the standby list. If you can still help, press the button below to confirm. The place is yours when you confirm.`,
    page: `A place has opened for this ${NOUN.en[kind]}. Can you still help?`,
    taken: 'Thank you for answering. That place has been taken; you are still on the standby list.',
  };
}
